import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Dialog } from './ui/Dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CertAPI } from '../lib/api';
import { useToast } from './ui/Toast';

interface Cert {
  domain: string;
  issuer?: string;
  expiresAt: string;
  daysLeft: number;
  autoRenew?: boolean;
}

interface CertCardProps {
  cert: Cert;
}

export function CertCard({ cert }: CertCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { toast } = useToast();
  const qc = useQueryClient();

  const renewMut = useMutation({
    mutationFn: () => CertAPI.renew(cert.domain),
    onSuccess: () => {
      toast(`Сертифікат для ${cert.domain} оновлено`, 'success');
      qc.invalidateQueries({ queryKey: ['certs'] });
      setConfirmOpen(false);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const daysVariant =
    cert.daysLeft < 14 ? 'error' : cert.daysLeft < 30 ? 'warning' : 'success';

  return (
    <>
      <div className="flex items-center justify-between py-2 border-b border-tg-border last:border-0">
        <div>
          <div className="text-sm font-medium text-tg-text">{cert.domain}</div>
          <div className="text-xs text-tg-text-secondary">{cert.issuer || '—'}</div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={daysVariant}>{cert.daysLeft}д</Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmOpen(true)}
            title="Оновити сертифікат"
          >
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Оновити сертифікат?"
      >
        <p className="text-sm text-tg-text-secondary mb-4">
          Сертифікат для <strong className="text-tg-text">{cert.domain}</strong> буде оновлено.
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setConfirmOpen(false)}>
            Скасувати
          </Button>
          <Button
            variant="success"
            loading={renewMut.isPending}
            onClick={() => renewMut.mutate()}
          >
            Оновити
          </Button>
        </div>
      </Dialog>
    </>
  );
}
